import { useCallback, useMemo } from 'react';
import { usePostChatRoomUrl } from '@/services/chat-room-url';
import { isInApp, openUrlInBrowser } from '@/libs/comeAppSDK';
import { track } from '@/utils/track';

// 上报策略：接口成功带真实 url 上报，接口失败/超时带空 url 上报
// 未传 source 时不执行上报（调用方无需感知页面来源的入口可不传）

/**
 * 联系客服的业务逻辑 Hook (Behavior Layer)
 *
 * @param type   - 客服类型（可选）：'1' 充值, '2' 提现
 * @param orderId - 订单号（可选），有订单场景下透传给接口
 * @param source  - 来源页面（可选），不传则不执行数据上报
 */
export const useContactCustomerService = ({ type, orderId, source }: { type?: '1' | '2'; orderId?: string; source?: string; } = {}) => {
  const handleSuccess = useCallback((url: string) => {
    console.log(url);
    if (source) {
      track('customer_service_click', {
        customer_service_url: url || '',
        current_page: source,
      });
    }
    if (url) {
      if (isInApp()) {
        openUrlInBrowser(url);
      } else {
        const link = document.createElement('a');
        link.href = url;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        setTimeout(() => {
          link.click();
          document.body.removeChild(link);
        }, 0);
      }
    }
  }, [source]);

  const mutationConfig = useMemo(
    () => ({
      onSuccess: handleSuccess,
      onError: (error: Error) => {
        console.error('Failed to get customer service URL:', error);
        if (source) {
          track('customer_service_click', {
            customer_service_url: '',
            current_page: source,
          });
        }
      },
    }),
    [handleSuccess, source]
  );

  const mutation = usePostChatRoomUrl({ mutationConfig });

  const handleContact = useCallback(
    (typeArg?: string, orderIdArg?: string) => {
      const newType = typeArg ?? type;
      const newOrderId = orderIdArg ?? orderId;

      mutation.mutate({
        params: {
          // 支持无参情况下直接打开通用客服
          ...(newType ? { type: newType } : {}),
          ...(newOrderId ? { orderId: newOrderId } : {}),
        },
      });
    },
    [mutation, type, orderId]
  );

  return useMemo(
    () => ({
      handleContact,
      isPending: mutation.isPending,
      isError: mutation.isError,
      error: mutation.error,
    }),
    [handleContact, mutation.isPending, mutation.isError, mutation.error]
  );
};
